import { useState, useEffect, useMemo } from "react";
import { GalleryAPI, resolveImageUrl } from "@/admin/api";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, ChevronRight, ChevronLeft, ChevronDown, ArrowRight, ArrowLeft, X, Maximize2, Phone, Clock, Car, MessageCircle, HelpCircle, Sparkles, ExternalLink, BookOpen } from "lucide-react";
import { CONTACT, IMG } from "@/veedu/data";
import { useOpeningHours } from "@/veedu/openingHours";
import { ChapterLabel } from "./Shared";
import { useReservation } from "@/context/ReservationContext";

/* ─────────────────────────────────────────────────────────────
   EXHIBIT DATA — Exact 5 Exhibits Structure & Descriptions
───────────────────────────────────────────────────────────── */
/* ═══════════════════════════════════════════════════════════════════════════════════════
   EXHIBIT CONFIGURATIONS — 5 Curated Exhibits Structure & Metadata
═══════════════════════════════════════════════════════════════════════════════════════ */
const EXHIBIT_CONFIGS = [
  {
    id: "journey",
    num: "01",
    label: "EXHIBIT 01 · ORIGINS & EVOLUTION",
    title: "THE JOURNEY",
    script: "Origins, experiences and the making of a chef.",
    desc: "From humble beginnings to a life shaped by food, tradition and curiosity. A collection of moments tracing the journey through kitchens, places and experiences.",
    fallbackHero: "/uploads/gallery/journey_01.jpg",
    fallbackCoverFrames: [
      { src: "/uploads/gallery/journey_01.jpg", caption: "Tharavadu Hearth", year: "HERITAGE" },
      { src: "/uploads/gallery/journey_02.jpg", caption: "Slow Cooking Tradition", year: "TRADITION" },
      { src: "/uploads/gallery/journey_03.jpg", caption: "Kerala Spice Coast", year: "ORIGINS" },
    ],
    fallbackPhotos: [
      { src: "/uploads/gallery/journey_01.jpg", title: "THARAVADU HEARTH", caption: "EARLY INFLUENCES & HERITAGE", year: "HERITAGE", size: "lg" },
      { src: "/uploads/gallery/journey_02.jpg", title: "BRASS URULI COOKING", caption: "SLOW COOKING TRADITION", year: "TRADITION", size: "md" },
      { src: "/uploads/gallery/journey_03.jpg", title: "KERALA COAST", caption: "FLAVOUR DISCOVERY & HERITAGE", year: "ORIGINS", size: "sm" },
    ],
  },
  {
    id: "moments",
    num: "02",
    label: "EXHIBIT 02 · EVENTS & CELEBRATIONS",
    title: "THE MOMENTS",
    script: "Celebrations, gatherings and unforgettable occasions.",
    desc: "Celebrations, gatherings and unforgettable occasions — the people and moments that have become part of the journey.",
    fallbackHero: "/uploads/gallery/moments_01.jpg",
    fallbackCoverFrames: [
      { src: "/uploads/gallery/moments_01.jpg", caption: "Onam Feast", year: "ANNUAL" },
      { src: "/uploads/gallery/moments_02.jpg", caption: "Nilavilakku Lighting", year: "CEREMONY" },
      { src: "/uploads/gallery/moments_03.jpg", caption: "Courtyard Gatherings", year: "CELEBRATION" },
    ],
    fallbackPhotos: [
      { src: "/uploads/gallery/moments_01.jpg", title: "ONAM AT THE VEEDU", caption: "ANNUAL 24-COURSE CELEBRATION", year: "ANNUAL", size: "lg" },
      { src: "/uploads/gallery/moments_02.jpg", title: "LIGHTING THE NILAVILAKKU", caption: "INAUGURAL CEREMONY", year: "CEREMONY", size: "md" },
      { src: "/uploads/gallery/moments_03.jpg", title: "COURTYARD CELEBRATION", caption: "MEMORABLE EVENINGS WITH GUESTS", year: "CELEBRATION", size: "sm" },
    ],
  },
  {
    id: "company",
    num: "03",
    label: "EXHIBIT 03 · PEOPLE & ENCOUNTERS",
    title: "IN GOOD COMPANY",
    script: "Encounters with remarkable people and shared conversations.",
    desc: "Encounters with remarkable people from food, film, art and culture — captured through shared tables, conversations and memorable occasions across the journey.",
    fallbackHero: "/uploads/gallery/company_02.jpg",
    fallbackCoverFrames: [
      { src: "/uploads/gallery/company_01.jpg", caption: "Distinguished Guests", year: "2023" },
      { src: "/uploads/gallery/company_03.jpg", caption: "Cultural Encounters", year: "2023" },
      { src: "/uploads/gallery/company_05.jpg", caption: "Evenings of Art & Music", year: "2022" },
    ],
    fallbackPhotos: [
      { src: "/uploads/gallery/company_01.jpg", title: "DISTINGUISHED ENCOUNTERS", caption: "CULTURAL ICONS & SHARED PASSION", year: "2023", size: "lg" },
      { src: "/uploads/gallery/company_02.jpg", title: "A CELEBRATION OF CRAFT", caption: "PRESENTING THE CULINARY CHRONICLE", year: "2022", size: "lg" },
      { src: "/uploads/gallery/company_03.jpg", title: "CONVERSATIONS IN CINEMA", caption: "CREATIVE MINDS AT THE TABLE", year: "2023", size: "md" },
      { src: "/uploads/gallery/company_04.jpg", title: "MEMORABLE ENCOUNTERS", caption: "STORIES, LAUGHTER & TRADITION", year: "2023", size: "md" },
      { src: "/uploads/gallery/company_05.jpg", title: "HARMONY OF FLAVOURS", caption: "AN EVENING OF MUSIC & MEMORIES", year: "2022", size: "sm" },
      { src: "/uploads/gallery/company_06.jpg", title: "WELCOMING TO THE TABLE", caption: "HOSPITALITY, WARMTH & HERITAGE", year: "2022", size: "sm" },
      { src: "/uploads/gallery/company_07.jpg", title: "GATHERINGS & CELEBRATION", caption: "MEMORABLE EVENINGS WITH FRIENDS", year: "2022", size: "md" },
    ],
  },
  {
    id: "kitchen",
    num: "04",
    label: "EXHIBIT 04 · FOOD & CRAFT",
    title: "FROM THE KITCHEN",
    script: "The craft, the fire, and the hands behind every creation.",
    desc: "Behind the pass and over roaring flames — a look into the kitchen where spices are tempered, dishes are plated with precision, and heritage flavours come alive.",
    fallbackHero: "/uploads/gallery/kitchen_01.png",
    fallbackCoverFrames: [
      { src: "/uploads/gallery/kitchen_01.png", caption: "Fire & Craft", year: "2018" },
      { src: "/uploads/gallery/kitchen_02.png", caption: "Plating Artistry", year: "2019" },
      { src: "/uploads/gallery/kitchen_03.png", caption: "At The Pass", year: "2019" },
    ],
    fallbackPhotos: [
      { src: "/uploads/gallery/kitchen_01.png", title: "MASTERY OVER FIRE", caption: "THE ART OF FLAME TOSSING & TEMPERING", year: "2018", size: "lg" },
      { src: "/uploads/gallery/kitchen_02.png", title: "PRECISION AT THE PASS", caption: "METICULOUS PLATING & CULINARY FOCUS", year: "2019", size: "lg" },
      { src: "/uploads/gallery/kitchen_03.png", title: "THE EXECUTIVE CHEF", caption: "INNOVATION ROOTED IN TRADITION", year: "2019", size: "md" },
      { src: "/uploads/gallery/kitchen_04.png", title: "THE HEARTH & THE FLAME", caption: "CAPTURING THE SOUL OF SOUTHERN SPICES", year: "2020", size: "md" },
      { src: "/uploads/gallery/kitchen_05.png", title: "THE FESTIVE TABLE", caption: "TRADITIONAL BANANA LEAF SADYA SPREAD", year: "2021", size: "sm" },
    ],
  },
  {
    id: "plate",
    num: "05",
    label: "EXHIBIT 05 · RECOGNITION & LEGACY",
    title: "BEYOND THE PLATE",
    script: "Awards, literary works, press and milestones beyond the kitchen.",
    desc: "Awards, published memoirs including A.T.T.I.T.U.D.E, media appearances and milestones that mark the journey beyond the kitchen.",
    fallbackHero: "/uploads/gallery/plate_01.jpg",
    fallbackCoverFrames: [
      { src: "/uploads/gallery/plate_02.jpg", caption: "A.T.T.I.T.U.D.E Memoir", year: "2026" },
      { src: "/uploads/gallery/plate_01.jpg", caption: "Milestone Awards", year: "2022" },
      { src: "/uploads/gallery/plate_03.jpg", caption: "Press & Media", year: "2021" },
    ],
    fallbackPhotos: [
      { src: "/uploads/gallery/plate_01.jpg", title: "CULINARY EXCELLENCE AWARD", caption: "RECOGNITION OF CRAFT", year: "2022", size: "lg" },
      { src: "/uploads/gallery/plate_02.jpg", title: "A.T.T.I.T.U.D.E — THE MEMOIR", caption: "PUBLISHED WITH EVEME & CARNET", year: "2026", size: "lg" },
      { src: "/uploads/gallery/plate_03.jpg", title: "GLOBAL FOOD SUMMIT", caption: "HERITAGE FLAVOURS ON THE WORLD STAGE", year: "2024", size: "md" },
    ],
  },
];

const DarkChocolateSeparator = () => (
  <div className="relative py-5 flex items-center justify-center">
    <div className="w-full h-[1px] bg-[#33271F]/40" />
    <div className="absolute px-4 bg-[#f5f0e8] text-[#c4a86a] font-serif text-xs flex items-center gap-2">
      <span className="w-6 h-px bg-[#33271F]/40" />
      <span className="text-[#c4a86a] select-none text-[10px]">✦</span>
      <span className="w-6 h-px bg-[#33271F]/40" />
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────
   SOCIAL MEDIA ICONS COMPONENT
───────────────────────────────────────────────────────────── */
const SocialIcons = () => (
  <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3">
    {/* Instagram */}
    <a
      href={CONTACT.instagram}
      target="_blank"
      rel="noreferrer"
      aria-label="Instagram"
      className="flex h-10 w-10 items-center justify-center rounded-full border border-[#1b3b28]/30 bg-[#1b3b28]/5 text-[#1b3b28] transition-all duration-300 hover:border-[#1b3b28] hover:bg-[#1b3b28] hover:text-[#f5f0e8] hover:scale-110 shadow-sm"
      title="Instagram (@chefjomon)"
    >
      <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    </a>

    {/* Facebook */}
    <a
      href={CONTACT.facebook}
      target="_blank"
      rel="noreferrer"
      aria-label="Facebook"
      className="flex h-10 w-10 items-center justify-center rounded-full border border-[#1b3b28]/30 bg-[#1b3b28]/5 text-[#1b3b28] transition-all duration-300 hover:border-[#1b3b28] hover:bg-[#1b3b28] hover:text-[#f5f0e8] hover:scale-110 shadow-sm"
      title="Facebook (Chef Jomon)"
    >
      <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    </a>

    {/* X / Twitter */}
    <a
      href={CONTACT.twitter}
      target="_blank"
      rel="noreferrer"
      aria-label="X (Twitter)"
      className="flex h-10 w-10 items-center justify-center rounded-full border border-[#1b3b28]/30 bg-[#1b3b28]/5 text-[#1b3b28] transition-all duration-300 hover:border-[#1b3b28] hover:bg-[#1b3b28] hover:text-[#f5f0e8] hover:scale-110 shadow-sm"
      title="X / Twitter (@chefjomon)"
    >
      <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    </a>

    {/* Wikipedia */}
    <a
      href={CONTACT.wikipedia}
      target="_blank"
      rel="noreferrer"
      aria-label="Wikipedia"
      className="flex h-10 w-10 items-center justify-center rounded-full border border-[#1b3b28]/30 bg-[#1b3b28]/5 text-[#1b3b28] font-serif font-bold text-sm transition-all duration-300 hover:border-[#1b3b28] hover:bg-[#1b3b28] hover:text-[#f5f0e8] hover:scale-110 shadow-sm"
      title="Wikipedia (Chef Jomon)"
    >
      W
    </a>

    {/* Official Website */}
    <a
      href={CONTACT.website}
      target="_blank"
      rel="noreferrer"
      aria-label="Official Website"
      className="flex h-10 w-10 items-center justify-center rounded-full border border-[#1b3b28]/30 bg-[#1b3b28]/5 text-[#1b3b28] transition-all duration-300 hover:border-[#1b3b28] hover:bg-[#1b3b28] hover:text-[#f5f0e8] hover:scale-110 shadow-sm"
      title="Official Website (chefjomon.com)"
    >
      <svg className="w-4 h-4 fill-none stroke-current" strokeWidth="1.8" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    </a>

    {/* WhatsApp */}
    <a
      href={CONTACT.whatsapp}
      target="_blank"
      rel="noreferrer"
      aria-label="WhatsApp Reserve"
      className="flex h-10 w-10 items-center justify-center rounded-full border border-[#1b3b28]/30 bg-[#1b3b28]/5 text-[#1b3b28] transition-all duration-300 hover:border-[#1b3b28] hover:bg-[#1b3b28] hover:text-[#f5f0e8] hover:scale-110 shadow-sm"
      title="WhatsApp (+44 7445 141 536)"
    >
      <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
      </svg>
    </a>

    {/* Founder's Book: ATTITUDE on EVEME */}
    <a
      href="https://eveme.uk/product/chef-jomons-attitude-pre-order/"
      target="_blank"
      rel="noreferrer"
      aria-label="Chef Jomon's Book A.T.T.I.T.U.D.E on EVEME"
      className="flex h-10 w-10 items-center justify-center rounded-full border border-[#c4a86a]/50 bg-[#c4a86a]/10 text-[#2a1f14] transition-all duration-300 hover:border-[#1b3b28] hover:bg-[#1b3b28] hover:text-[#f5f0e8] hover:scale-110 shadow-sm"
      title="Chef Jomon's Book: A.T.T.I.T.U.D.E (EVEME Pre-Order)"
    >
      <BookOpen size={16} />
    </a>
  </div>
);

/* ─────────────────────────────────────────────────────────────
   HOUSE NOTES & LOCAL DINING GUIDE (Interactive Almanac & Concierge)
───────────────────────────────────────────────────────────── */
const FAQS = [
  {
    id: "location",
    category: "visit",
    num: "01",
    badge: "Location & Access",
    q: "Where is Chef Jomon's #VEEDU located and how do I get there?",
    a: "Chef Jomon's #VEEDU is situated in the historic town centre of Hornchurch at 197-201 High Street, Hornchurch, Essex / London Borough of Havering, RM11 3XT. We are conveniently situated for diners travelling from Romford, Upminster, Gidea Park, Elm Park, and across East London. Hornchurch Underground Station (District Line) is just a short walk away.",
    action: {
      label: "Open in Google Maps",
      href: CONTACT.maps,
      isExternal: true,
      note: "197-201 High Street, RM11 3XT · 7 min walk from Hornchurch Station",
    },
  },
  {
    id: "cuisine",
    category: "dining",
    num: "02",
    badge: "Veg & Non-Veg Menus",
    q: "What style of cuisine does #VEEDU serve? Do you cater for both vegetarian and non-vegetarian diets?",
    a: "We celebrate authentic Kerala culinary heritage, coastal Malabar seafood, and South Indian regional cooking, alongside contemporary pan-Indian influences. We take immense pride in offering an extensive selection for both vegetarian and non-vegetarian diners—featuring signature coastal fish (Meen Pollichathu), tender slow-roasted beef and lamb shank, alongside dedicated vegetarian, vegan, and gluten-free traditional Keralan classics like Avial, Thoran, Vegetable Stew, and fresh Appams.",
    action: {
      label: "Explore Our Menu Journey",
      href: "#table",
      isExternal: false,
      note: "Coastal Malabar Seafood, Tandoor Grills & Traditional Vegetarian Specialties",
    },
  },
  {
    id: "takeaway",
    category: "dining",
    num: "03",
    badge: "Dine-In & Takeaway",
    q: "Is both dine-in dining and takeaway available?",
    a: "Yes! Guests can enjoy an immersive dine-in experience within our historic timber-framed Veedu, or order our Kerala curries, biriyanis, and South Indian specialities for takeaway and collection to enjoy at home across Hornchurch, Havering, and surrounding East London neighbourhoods.",
    action: {
      label: "Order / Enquire on WhatsApp",
      href: CONTACT.whatsapp,
      isExternal: true,
      note: "Freshly prepared takeaway & collection available Tuesday to Sunday",
    },
  },
  {
    id: "booking",
    category: "visit",
    num: "04",
    badge: "Table Booking",
    q: "Are table reservations available and recommended?",
    a: "Table reservations will open shortly ahead of our official opening date. Guests will be able to reserve tables directly, contact our reservations desk (+44 7445 141 536), or explore our architectural Floor Plan to choose their preferred seating area. Pre-opening enquiries for private gatherings and launch tastings are currently welcome.",
    action: {
      label: "Reserve Table (Opening Soon)",
      isReservationAction: true,
      note: "Reservations opening soon · Priority launch seating preview",
    },
  },
  {
    id: "bar-dj",
    category: "dining",
    num: "05",
    badge: "Bar & DJ Floor",
    q: "Do you have bar counters and a DJ floor for evening entertainment?",
    a: "Yes! #VEEDU features an artisanal bar counter serving curated cocktails, Indian botanical spirits, fine wines, and aperitifs. We also feature a dedicated DJ floor and evening music space, making #VEEDU a prime venue for celebratory dinners, private parties, and lively weekend gatherings in Hornchurch.",
    action: {
      label: "Enquire for Parties & Events",
      href: CONTACT.whatsapp,
      isExternal: true,
      note: "Craft cocktails, botanical spirits & weekend celebratory evenings",
    },
  },
  {
    id: "parking",
    category: "visit",
    num: "06",
    badge: "Fentiman Way Parking",
    q: "Is car parking available near the restaurant?",
    a: "Yes! Ample car parking is conveniently located just a 2-minute stroll away at Fentiman Way Car Park (RM11 1QX), situated directly off Hornchurch High Street. There is also pay-and-display parking along the High Street and adjacent town centre car parks.",
    action: {
      label: "Navigate to Car Park (RM11 1QX)",
      href: CONTACT.maps,
      isExternal: true,
      note: "Fentiman Way Car Park · 2 minutes flat walk to our entrance",
    },
  },
];

const FAQ_TABS = [
  { id: "all", label: "All House Notes", count: 6 },
  { id: "dining", label: "Dining & Menus", count: 3 },
  { id: "visit", label: "Visiting & Access", count: 3 },
];

const HouseNotesFAQ = () => {
  const { openReservationModal } = useReservation();
  const [activeTab, setActiveTab] = useState("all");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [openMobileSet, setOpenMobileSet] = useState(() => new Set([0]));

  const filteredFaqs = activeTab === "all" ? FAQS : FAQS.filter((f) => f.category === activeTab);
  const activeFaq = filteredFaqs[selectedIdx] || filteredFaqs[0];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSelectedIdx(0);
  };

  const toggleMobile = (idx) => {
    setOpenMobileSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const handleNextFaq = () => {
    setSelectedIdx((prev) => (prev + 1) % filteredFaqs.length);
  };

  const handlePrevFaq = () => {
    setSelectedIdx((prev) => (prev - 1 + filteredFaqs.length) % filteredFaqs.length);
  };

  return (
    <div data-testid="house-notes-faq" className="mt-14 pt-10 border-t border-[#33271F]/20">
      {/* ── SECTION HEADER ── */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 text-left">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="h-px w-6 bg-[#c4a86a]" />
            <p className="font-body text-[9.5px] tracking-[0.35em] uppercase text-[#b89a60] font-bold">
              HOUSE NOTES &amp; DINING COMPANION
            </p>
          </div>
          <h3 className="font-display text-2xl sm:text-3xl md:text-4xl font-light text-[#2a1f14] tracking-tight">
            Frequently Asked Questions
          </h3>
          <p className="mt-1.5 font-sans text-xs md:text-sm text-[#6a5442] max-w-xl leading-relaxed">
            Essential guidelines on table reservations, regional Kerala gastronomy, parking access, and celebrating with us at 197 High Street.
          </p>
        </div>

        {/* Category Filter Pills */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar shrink-0">
          {FAQ_TABS.map((tab) => {
            const isTabActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[10.5px] font-sans font-bold tracking-[0.15em] uppercase transition-all duration-300 border ${
                  isTabActive
                    ? "bg-[#1b3b28] text-[#f5f0e8] border-[#1b3b28] shadow-sm"
                    : "bg-[#ebd9c5]/30 text-[#5a4535] border-[#33271F]/15 hover:border-[#33271F]/30 hover:bg-[#ebd9c5]/60"
                }`}
              >
                <span>{tab.label}</span>
                <span
                  className={`text-[9px] px-1.5 py-0.2 rounded-full font-mono ${
                    isTabActive ? "bg-[#c4a86a] text-[#1b3b28]" : "bg-[#33271F]/10 text-[#5a4535]"
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          DESKTOP MASTER-DETAIL CONCIERGE DESK (lg & up)
      ───────────────────────────────────────────────────────────── */}
      <div className="hidden lg:grid lg:grid-cols-12 gap-6 items-stretch">
        {/* Left Column: Interactive Directory Index (5 Cols) */}
        <div className="lg:col-span-5 flex flex-col justify-between rounded-2xl border border-[#33271F]/20 bg-[#ede4d7]/70 backdrop-blur-sm shadow-sm overflow-hidden p-2">
          <div>
            <div className="px-4 py-3 border-b border-[#33271F]/15 flex items-center justify-between">
              <span className="font-mono text-[9.5px] tracking-[0.25em] uppercase font-bold text-[#8c6d48] flex items-center gap-1.5">
                <span className="text-[#c4a86a]">✦</span> DIRECTORY INDEX
              </span>
              <span className="font-mono text-[9px] uppercase tracking-wider text-[#5a4535]/80">
                {filteredFaqs.length} Entries
              </span>
            </div>

            <div className="divide-y divide-[#33271F]/10">
              {filteredFaqs.map((faq, idx) => {
                const isSelected = selectedIdx === idx;
                return (
                  <button
                    key={faq.id}
                    data-testid={`faq-item-${idx}`}
                    data-faq-toggle={`faq-toggle-${idx}`}
                    onClick={() => setSelectedIdx(idx)}
                    className={`w-full text-left p-4 rounded-xl transition-all duration-300 flex items-start gap-3.5 group relative ${
                      isSelected
                        ? "bg-[#1b3b28] text-[#f5f0e8] shadow-md"
                        : "hover:bg-[#ebd9c5]/50 text-[#2a1f14]"
                    }`}
                  >
                    {/* Active Accent Bar */}
                    {isSelected && (
                      <motion.div
                        layoutId="activeFaqIndicator"
                        className="absolute left-1 top-2.5 bottom-2.5 w-1 rounded-full bg-[#c4a86a]"
                      />
                    )}

                    {/* Number Badge */}
                    <span
                      className={`font-mono text-xs font-bold shrink-0 pt-0.5 ${
                        isSelected ? "text-[#c4a86a]" : "text-[#8c6d48]"
                      }`}
                    >
                      {faq.num}
                    </span>

                    {/* Title and Category */}
                    <div className="flex-1 min-w-0 pr-1">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-[8px] font-sans font-bold tracking-[0.15em] uppercase mb-1.5 ${
                          isSelected
                            ? "bg-[#c4a86a]/25 text-[#e8ded1]"
                            : "bg-[#1b3b28]/10 text-[#1b3b28]"
                        }`}
                      >
                        {faq.badge}
                      </span>
                      <h4
                        className={`font-sans text-[13.5px] font-semibold leading-snug line-clamp-2 ${
                          isSelected ? "text-[#f5f0e8]" : "text-[#2a1f14] group-hover:text-[#1b3b28]"
                        }`}
                      >
                        {faq.q}
                      </h4>
                    </div>

                    {/* Arrow indicator */}
                    <ChevronRight
                      size={16}
                      className={`shrink-0 transition-transform duration-300 mt-1 ${
                        isSelected
                          ? "text-[#c4a86a] translate-x-0.5"
                          : "text-[#33271F]/30 group-hover:text-[#33271F]/60"
                      }`}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Concierge Help Desk Mini-Strip */}
          <div className="mt-3 p-3.5 rounded-xl bg-[#1b3b28]/10 border border-[#1b3b28]/20 flex items-center justify-between text-left">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-full bg-[#1b3b28] text-[#f5f0e8]">
                <HelpCircle size={14} />
              </div>
              <div>
                <p className="font-sans text-[11px] font-bold text-[#1b3b28] uppercase tracking-wider">
                  Have a custom inquiry?
                </p>
                <p className="font-sans text-[10px] text-[#6a5442]">
                  Direct host assistance
                </p>
              </div>
            </div>
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noreferrer"
              className="px-3 py-1.5 rounded-full bg-[#1b3b28] text-[#f5f0e8] font-sans text-[9.5px] font-bold tracking-wider uppercase hover:bg-[#142e1f] transition-colors"
            >
              Ask via WhatsApp
            </a>
          </div>
        </div>

        {/* Right Column: The Architectural Plaque Answer Card (7 Cols) */}
        <div className="lg:col-span-7 rounded-2xl border border-[#33271F]/20 bg-[#fdfbf7] p-8 lg:p-9 shadow-[0_8px_30px_rgba(40,25,15,0.06)] relative overflow-hidden flex flex-col justify-between text-left">
          {/* Faint Watermark Decorative Number */}
          <span className="font-display text-9xl text-[#c4a86a]/15 absolute top-2 right-6 pointer-events-none select-none font-bold leading-none">
            {activeFaq.num}
          </span>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeFaq.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
              className="relative z-10 flex-1 flex flex-col justify-between"
            >
              {/* Header Meta */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2.5 py-0.5 rounded-full text-[9px] font-sans font-bold tracking-[0.2em] uppercase bg-[#1b3b28] text-[#f5f0e8]">
                    {activeFaq.badge}
                  </span>
                  <span className="text-[10px] font-mono text-[#8c6d48] tracking-widest uppercase">
                    • NOTE {activeFaq.num} OF {String(filteredFaqs.length).padStart(2, "0")}
                  </span>
                </div>

                {/* Question Title */}
                <h3 className="font-display text-2xl lg:text-3xl font-normal text-[#2a1f14] leading-snug tracking-tight">
                  {activeFaq.q}
                </h3>

                {/* Subtle Divider */}
                <div className="w-16 h-px bg-gradient-to-r from-[#c4a86a] to-transparent my-4" />

                {/* Answer Prose */}
                <p className="font-sans text-[14.5px] lg:text-[15.5px] leading-relaxed text-[#503d2e] font-normal">
                  {activeFaq.a}
                </p>
              </div>

              {/* Contextual Action Banner */}
              <div className="mt-8 pt-5 border-t border-[#33271F]/15 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-[#f5ede1]/60 p-4 rounded-xl border border-[#33271F]/10">
                <div className="flex items-center gap-2.5 text-[#6a5442]">
                  <Sparkles size={16} className="text-[#c4a86a] shrink-0" />
                  <span className="font-sans text-xs font-medium text-[#4a3b2c]">
                    {activeFaq.action.note}
                  </span>
                </div>

                {activeFaq.action.isReservationAction ? (
                  <button
                    type="button"
                    onClick={() => openReservationModal()}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1b3b28] text-[#f5f0e8] font-sans text-[10.5px] font-bold tracking-[0.18em] uppercase hover:bg-[#142e1f] transition-all hover:scale-[1.02] shadow-sm shrink-0 cursor-pointer"
                  >
                    <span>{activeFaq.action.label}</span>
                    <Sparkles size={12} className="text-[#c4a86a]" />
                  </button>
                ) : activeFaq.action.isExternal ? (
                  <a
                    href={activeFaq.action.href}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1b3b28] text-[#f5f0e8] font-sans text-[10.5px] font-bold tracking-[0.18em] uppercase hover:bg-[#142e1f] transition-all hover:scale-[1.02] shadow-sm shrink-0"
                  >
                    <span>{activeFaq.action.label}</span>
                    <ExternalLink size={12} />
                  </a>
                ) : (
                  <a
                    href={activeFaq.action.href}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1b3b28] text-[#f5f0e8] font-sans text-[10.5px] font-bold tracking-[0.18em] uppercase hover:bg-[#142e1f] transition-all hover:scale-[1.02] shadow-sm shrink-0"
                  >
                    <span>{activeFaq.action.label}</span>
                    <ArrowRight size={12} />
                  </a>
                )}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Footer Prev / Next Controls */}
          <div className="mt-6 pt-4 border-t border-[#33271F]/10 flex items-center justify-between text-xs text-[#8c6d48]">
            <span className="font-mono text-[10px] tracking-wider uppercase">
              Select any note on left or use arrows
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handlePrevFaq}
                aria-label="Previous question"
                className="p-2 rounded-full border border-[#33271F]/20 bg-[#f5ede1] hover:bg-[#1b3b28] hover:text-[#f5f0e8] transition-colors"
              >
                <ChevronLeft size={15} />
              </button>
              <button
                type="button"
                onClick={handleNextFaq}
                aria-label="Next question"
                className="p-2 rounded-full border border-[#33271F]/20 bg-[#f5ede1] hover:bg-[#1b3b28] hover:text-[#f5f0e8] transition-colors"
              >
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          MOBILE & TABLET SINGLE-FRAME ARCHITECTURAL LEDGER (< lg)
      ───────────────────────────────────────────────────────────── */}
      <div className="lg:hidden rounded-2xl border border-[#33271F]/20 bg-[#fdfbf7] overflow-hidden shadow-sm divide-y divide-[#33271F]/15 text-left">
        {filteredFaqs.map((faq, idx) => {
          const isOpen = openMobileSet.has(idx);
          return (
            <div
              key={faq.id}
              data-testid={`faq-item-${idx}`}
              className={`transition-colors duration-300 ${
                isOpen ? "bg-[#f7f0e4]/80" : "hover:bg-[#f7f0e4]/40"
              }`}
            >
              <button
                type="button"
                data-testid={`faq-toggle-${idx}`}
                onClick={() => toggleMobile(idx)}
                className="w-full flex items-start justify-between gap-3.5 p-4 sm:p-5 text-left cursor-pointer select-none"
                aria-expanded={isOpen}
              >
                <div className="flex items-start gap-3 flex-1 min-w-0 pr-2">
                  <span className="font-mono text-xs font-bold text-[#c4a86a] shrink-0 pt-0.5">
                    {faq.num}
                  </span>
                  <div>
                    <span className="inline-block px-2.5 py-0.5 rounded-full text-[8.5px] font-sans font-bold tracking-[0.18em] uppercase bg-[#1b3b28]/10 text-[#1b3b28] mb-1.5 border border-[#1b3b28]/20">
                      {faq.badge}
                    </span>
                    <h4 className="font-sans text-sm sm:text-[15px] font-semibold text-[#2a1f14] leading-snug">
                      {faq.q}
                    </h4>
                  </div>
                </div>

                <div
                  className={`p-1.5 rounded-full mt-0.5 shrink-0 transition-all duration-300 ${
                    isOpen
                      ? "bg-[#1b3b28] text-[#f5f0e8] rotate-180 shadow-sm"
                      : "bg-[#33271F]/10 text-[#5a4535]"
                  }`}
                >
                  <ChevronDown size={15} strokeWidth={2.2} />
                </div>
              </button>

              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4 sm:px-5 sm:pb-5 pt-0 border-t border-[#33271F]/10 text-xs sm:text-[13.5px] leading-relaxed text-[#503d2e] font-sans pl-10 sm:pl-11">
                      <p className="pt-3">{faq.a}</p>

                      {/* Mobile Contextual Quick Action */}
                      <div className="mt-4 pt-3 border-t border-[#33271F]/10 flex flex-col gap-2">
                        <span className="text-[11px] text-[#8c6d48] font-medium">
                          {faq.action.note}
                        </span>
                        <div>
                          {faq.action.isReservationAction ? (
                            <button
                              type="button"
                              onClick={() => openReservationModal()}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1b3b28] text-[#f5f0e8] font-sans text-[10px] font-bold tracking-wider uppercase hover:bg-[#142e1f] transition-all shadow-sm cursor-pointer"
                            >
                              <span>{faq.action.label}</span>
                              <Sparkles size={12} className="text-[#c4a86a]" />
                            </button>
                          ) : faq.action.isExternal ? (
                            <a
                              href={faq.action.href}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1b3b28] text-[#f5f0e8] font-sans text-[10px] font-bold tracking-wider uppercase hover:bg-[#142e1f] transition-all shadow-sm"
                            >
                              <span>{faq.action.label}</span>
                              <ExternalLink size={12} />
                            </a>
                          ) : (
                            <a
                              href={faq.action.href}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1b3b28] text-[#f5f0e8] font-sans text-[10px] font-bold tracking-wider uppercase hover:bg-[#142e1f] transition-all shadow-sm"
                            >
                              <span>{faq.action.label}</span>
                              <ArrowRight size={12} />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   VISIT FOOTER — Frameless Premium Design with Emerald Accents
───────────────────────────────────────────────────────────── */
const VisitFooter = () => {
  const { openReservationModal } = useReservation();
  const status = useOpeningHours();

  return (
    <footer className="border-t border-[#33271F]/30 bg-[#f5f0e8] text-[#2a1f14]">
      <div className="w-full px-6 md:pl-[12%] lg:pl-[14%] xl:pl-[16%] pr-6 md:pr-12 lg:pr-16 py-14 md:py-18">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          
          {/* Column 1: Chef Jomon Founder / Director (Frameless & Organic) */}
          <div className="lg:col-span-5 flex flex-col items-center lg:items-start text-center lg:text-left lg:border-r lg:border-[#33271F]/20 lg:pr-10">
            <img
              src="/Images/logo_new_black.png"
              alt="Chef Jomon Logo"
              className="h-20 max-h-24 w-auto object-contain mb-5 opacity-95 hover:opacity-100 transition-opacity"
            />

            <span className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-[#1b3b28] text-[#e8ded1] text-[9.5px] font-sans font-bold tracking-[0.25em] uppercase mb-4 shadow-sm">
              <span>🌿</span> KERALA HERITAGE CUISINE
            </span>

            <img 
              src="/Images/Chef Jomon Text.png" 
              alt="Chef Jomon" 
              className="h-12 md:h-16 w-auto object-contain mt-2 mb-1" 
            />
            <p className="mt-1.5 font-sans text-xs tracking-[0.2em] text-[#8c6d48] uppercase font-semibold">
              Director of Culinary <span className="mx-1 opacity-40">|</span> Founder
            </p>

            <div className="w-16 h-px bg-gradient-to-r from-[#c4a86a] to-transparent my-5" />

            {/* Phone Numbers */}
            <div className="flex flex-col gap-2.5 font-sans text-sm md:text-base font-semibold text-[#2a1f14]">
              <a
                href={CONTACT.mobileHref}
                className="inline-flex items-center gap-2.5 text-[#1b3b28] hover:text-[#c4a86a] transition-colors"
              >
                <Phone size={15} className="text-[#1b3b28]" />
                <MessageCircle size={15} className="text-[#1b3b28]" />
                <span className="tracking-wide">{CONTACT.mobile}</span>
              </a>
              <a
                href={CONTACT.phoneHref}
                className="inline-flex items-center gap-2.5 text-[#2a1f14] hover:text-[#c4a86a] transition-colors"
              >
                <Phone size={15} className="text-[#c4a86a]" />
                <span className="tracking-wide">{CONTACT.phone}</span>
              </a>
            </div>

            <div className="mt-6 w-full">
              <SocialIcons />
            </div>
          </div>

          {/* Column 2: Address, Hours & Live UK Status, Parking, Map Link */}
          <div className="lg:col-span-7 flex flex-col gap-5 text-left">
            <div>
              <p className="font-body text-[9.5px] tracking-[0.45em] uppercase text-[#b89a60] font-bold mb-1">
                COME HOME TO THE TABLE
              </p>
              <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-light text-[#2a1f14] leading-tight">
                VISIT #VEEDU
              </h3>
            </div>

            <div className="flex flex-col gap-3 font-sans text-sm text-[#5a4535]">
              {/* Address with Google Maps link */}
              <a
                href={CONTACT.maps}
                target="_blank"
                rel="noreferrer"
                className="group flex items-start gap-3 hover:text-[#1b3b28] transition-colors"
              >
                <div className="p-2 rounded-full bg-[#1b3b28]/10 text-[#1b3b28] group-hover:bg-[#1b3b28] group-hover:text-[#f5f0e8] transition-all shrink-0 mt-0.5">
                  <MapPin size={16} strokeWidth={2} />
                </div>
                <span className="pt-0.5">
                  <strong className="text-[#2a1f14] font-semibold">Address:</strong> {CONTACT.address}
                  <span className="ml-2 text-xs text-[#1b3b28] font-bold underline underline-offset-2 hover:text-[#c4a86a]">Open in Google Maps ↗</span>
                </span>
              </a>

              {/* Parking info */}
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-full bg-[#c4a86a]/15 text-[#8c6d48] shrink-0 mt-0.5">
                  <Car size={16} strokeWidth={2} />
                </div>
                <span className="pt-0.5">
                  <strong className="text-[#2a1f14] font-semibold">Parking:</strong> {CONTACT.parking}
                </span>
              </div>
            </div>

            {/* Responsive Opening Hours & Live UK Status Card */}
            <div data-testid="opening-hours-card" className="mt-1 rounded-xl border border-[#33271F]/20 bg-[#ebd9c5]/25 p-4 md:p-5 backdrop-blur-sm">
              <div className="flex flex-wrap items-center justify-between gap-2.5 pb-3 border-b border-[#33271F]/15">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-full bg-[#1b3b28] text-[#f5f0e8]">
                    <Clock size={15} strokeWidth={2} />
                  </div>
                  <div>
                    <h4 className="font-display text-base md:text-lg font-semibold tracking-tight text-[#2a1f14]">
                      Opening Hours
                    </h4>
                    <p className="font-sans text-[10px] text-[#8c6d48] font-medium tracking-wider uppercase">
                      UK Local Time (Europe/London)
                    </p>
                  </div>
                </div>

                {/* Live Status Badge */}
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10.5px] font-sans font-bold tracking-wider uppercase shadow-sm ${
                  status.isOpen
                    ? status.isLastOrder
                      ? "bg-amber-100 text-amber-900 border border-amber-300"
                      : "bg-[#1b3b28] text-[#f5f0e8]"
                    : "bg-[#33271F]/10 text-[#5a4535] border border-[#33271F]/20"
                }`}>
                  <span className={`h-2 w-2 rounded-full ${
                    status.isOpen
                      ? status.isLastOrder
                        ? "bg-amber-500 animate-pulse"
                        : "bg-emerald-400 animate-pulse"
                      : "bg-red-500"
                  }`} />
                  <span>{status.statusLabel}</span>
                  <span className="opacity-40">•</span>
                  <span className="font-mono text-xs">{status.currentUKTime} UK</span>
                </div>
              </div>

              {/* Schedule Table (24-Hour Railway Time) */}
              <div className="mt-3.5 space-y-2.5 font-sans text-xs md:text-sm">
                {/* Monday */}
                <div className="flex items-center justify-between py-1 border-b border-[#33271F]/10">
                  <span className="font-semibold text-[#2a1f14]">Monday</span>
                  <span className="px-2.5 py-0.5 rounded text-[11px] font-bold bg-[#33271F]/10 text-[#8c6d48] uppercase tracking-wider">
                    Closed
                  </span>
                </div>

                {/* Tuesday - Friday */}
                <div className="flex items-center justify-between py-1 border-b border-[#33271F]/10">
                  <span className="font-semibold text-[#2a1f14]">Tuesday – Friday</span>
                  <div className="text-right">
                    <span className="font-mono font-bold text-[#1b3b28]">16:30 – 23:00</span>
                    <span className="block text-[10.5px] text-[#8c6d48] font-medium">
                      (Last order: <span className="font-mono font-semibold">22:30</span>)
                    </span>
                  </div>
                </div>

                {/* Saturday - Sunday */}
                <div className="flex items-center justify-between py-1">
                  <span className="font-semibold text-[#2a1f14]">Saturday – Sunday</span>
                  <div className="text-right">
                    <span className="font-mono font-bold text-[#1b3b28]">13:00 – 23:00</span>
                  </div>
                </div>
              </div>

              {/* Next Opening & Order Info */}
              <div className="mt-3.5 pt-2.5 border-t border-[#33271F]/15 flex items-center justify-between text-[11px] font-sans">
                <span className="text-[#5a4535] font-medium">Status Info:</span>
                <span className="font-semibold text-[#1b3b28] italic">{status.nextOpening}</span>
              </div>
            </div>

            {/* CTA + Quote */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-2 pt-4 border-t border-[#33271F]/20">
              <button
                type="button"
                onClick={() => openReservationModal()}
                className="inline-flex items-center gap-3 px-8 py-3.5 bg-[#1b3b28] text-[#f5f0e8] font-sans text-[11px] font-bold tracking-[0.25em] uppercase hover:bg-[#142e1f] transition-all duration-300 rounded-full shadow-md hover:shadow-lg hover:scale-[1.02] cursor-pointer"
              >
                <span>Reserve Table</span> <ArrowRight size={14} />
              </button>
              <p className="font-cursive text-base md:text-lg text-[#1b3b28] italic font-medium">
                "Some places you visit, some places you feel. This is home."
              </p>
            </div>
          </div>

        </div>

        {/* ── HOUSE NOTES & LOCAL DINING GUIDE (Collapsible FAQ) ── */}
        <HouseNotesFAQ />
      </div>

      {/* Bottom Keywords & Copyright Strip */}
      <div className="border-t border-[#33271F]/30 bg-[#1b3b28] py-7 px-6 text-center text-[#e8ded1]">
        <div className="flex items-center justify-center gap-2 text-[#c4a86a] text-xs mb-2">
          <span>✦</span>
          <span>✦</span>
          <span>✦</span>
        </div>
        <p className="font-sans text-[10px] md:text-[11.5px] font-bold tracking-[0.3em] uppercase text-[#e8ded1]">
          VIBRANT <span className="mx-2 text-[#c4a86a]">|</span> EARTHY <span className="mx-2 text-[#c4a86a]">|</span> ENTICING <span className="mx-2 text-[#c4a86a]">|</span> DELECTABLE <span className="mx-2 text-[#c4a86a]">|</span> UMAMI
        </p>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 font-sans text-[9.5px] tracking-widest text-[#e8ded1]/70 uppercase">
          <span>© {new Date().getFullYear()} Chef Jomon's Veedu. All rights reserved.</span>
          <span className="opacity-40">•</span>
          <a
            href="/admin/login"
            data-testid="footer-admin-link"
            className="text-[#c4a86a]/90 hover:text-[#c4a86a] transition-colors underline-offset-4 hover:underline font-medium tracking-[0.2em]"
          >
            Admin
          </a>
        </div>
      </div>
    </footer>
  );
};

/* ─────────────────────────────────────────────────────────────
   LARGE EDITORIAL EXHIBIT CARD COMPONENT (Matching Screenshot Layout)
───────────────────────────────────────────────────────────── */
const EditorialExhibitCard = ({ exhibit, onClick, isWide = false }) => {
  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      onClick={onClick}
      className={`group relative cursor-pointer overflow-hidden rounded-2xl border border-[#33271F] bg-[#1c120c] p-6 md:p-8 text-[#f5f0e8] shadow-[0_10px_30px_rgba(0,0,0,0.35)] transition-all duration-500 hover:border-[#c4a86a]/80 hover:shadow-[0_16px_40px_rgba(0,0,0,0.5)] flex flex-col justify-between min-h-[300px] md:min-h-[340px] ${
        isWide ? "col-span-1 md:col-span-6" : "col-span-1 md:col-span-4"
      }`}
    >
      {/* Background Media with Dark Vignette Overlay */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <img
          src={exhibit.hero}
          alt={exhibit.title}
          className="h-full w-full object-cover opacity-35 transition-transform duration-700 ease-out group-hover:scale-105 group-hover:opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#160e09] via-[#160e09]/75 to-[#160e09]/40" />
        <div className="absolute inset-0 shadow-[inset_0_0_80px_rgba(0,0,0,0.8)]" />
      </div>

      {/* Top Section: Exhibit Number & Floating Polaroids Stack */}
      <div className="relative z-10 flex items-start justify-between">
        <span className="font-display text-4xl md:text-5xl font-light text-[#c4a86a]/80 group-hover:text-[#c4a86a] transition-colors">
          {exhibit.num}
        </span>

        {/* Stacked Polaroids Decoration */}
        <div className="flex -space-x-4 overflow-hidden pt-1 pr-2">
          {exhibit.coverFrames.map((f, i) => (
            <div
              key={i}
              className="relative h-14 w-12 md:h-16 md:w-14 shrink-0 rounded border-[3px] border-[#f5f0e8] bg-[#e8e0d0] shadow-md transition-transform duration-500 group-hover:rotate-2 group-hover:scale-105"
              style={{
                transform: `rotate(${i === 0 ? -6 : i === 1 ? 4 : -2}deg)`,
              }}
            >
              <img src={f.src} alt={f.caption} className="h-full w-full object-cover" />
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Section: Title, Description & CTA */}
      <div className="relative z-10 mt-6 flex flex-col justify-end">
        <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-[#f5f0e8] group-hover:text-[#e8d19a] transition-colors">
          {exhibit.title}
        </h3>
        <p className="mt-1.5 font-body text-xs md:text-sm font-light leading-relaxed text-[#d9cfc1]/80 max-w-md line-clamp-3">
          {exhibit.desc}
        </p>

        <div className="mt-5 flex items-center gap-2 font-sans text-[10px] md:text-[11px] font-semibold tracking-[0.25em] uppercase text-[#c4a86a] group-hover:text-[#f4ebdd] transition-colors">
          <span>VIEW EXHIBIT</span>
          <span className="transition-transform duration-300 group-hover:translate-x-1.5">→</span>
        </div>
      </div>
    </motion.div>
  );
};

/* ─────────────────────────────────────────────────────────────
   INDIVIDUAL ALBUM VIEW COMPONENT (Medium Space-Efficient Header)
───────────────────────────────────────────────────────────── */
const IndividualExhibitView = ({ exhibit, onBack, onOpenLightbox }) => {
  const isCompany = exhibit.id === "company";

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -15 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="w-full px-4 sm:px-6 md:pl-[12%] lg:pl-[14%] xl:pl-[16%] pr-4 sm:pr-6 md:pr-12 lg:pr-16 py-6 md:py-8"
    >
      {/* Back Button */}
      <button
        onClick={onBack}
        className="mb-5 inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#33271F] bg-[#33271F]/5 text-[#3a2a1a] font-sans text-[9px] md:text-[10px] tracking-[0.2em] uppercase font-bold hover:bg-[#33271F] hover:text-[#f5f0e8] transition-all duration-300"
      >
        <ArrowLeft size={12} /> BACK TO GALLERY
      </button>

      {/* Header Info — Compact & Medium Sized */}
      <div className="mb-6 md:mb-8 border-b border-[#33271F]/20 pb-5 md:pb-6">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-display text-base font-light text-[#c4a86a]">{exhibit.num}</span>
          <span className="h-px w-5 bg-[#c4a86a]/40" />
          <p className="font-body text-[8.5px] tracking-[0.35em] uppercase text-[#b89a60]">
            {exhibit.label}
          </p>
        </div>

        <h1 className="font-display text-2xl md:text-4xl font-normal leading-tight text-[#2a1f14]">
          {exhibit.title}
        </h1>

        <p className="font-cursive text-lg md:text-xl text-[#c4a86a] italic mt-1">
          "{exhibit.script}"
        </p>

        <p className="mt-2 font-body text-xs md:text-sm leading-relaxed text-[#5a4535]/90 max-w-xl">
          {exhibit.desc}
        </p>
      </div>

      {/* Photo Archive Exhibition Grid */}
      <div
        className={`grid ${
          isCompany
            ? "grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5"
            : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
        }`}
      >
        {exhibit.photos.map((photo, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: i * 0.05 }}
            className="group cursor-pointer flex flex-col items-center w-full"
            onClick={() => onOpenLightbox(photo)}
          >
            {/* Framed Image Container */}
            <div
              className={`relative w-full aspect-[4/3] ${
                isCompany
                  ? "rounded-md border-[4px] sm:border-[5px] md:border-[6px] border-[#f5f0e8]"
                  : "rounded border-[8px] border-[#f5f0e8]"
              } bg-[#e8e0d0] overflow-hidden transition-all duration-500 group-hover:scale-[1.02]`}
              style={{
                boxShadow: "3px 4px 14px rgba(40,25,15,0.12), 0 2px 4px rgba(40,25,15,0.06)",
                outline: "1px solid rgba(180,155,100,0.3)",
              }}
            >
              <img
                src={photo.src}
                alt={photo.title}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <Maximize2 size={18} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 sm:w-[22px] sm:h-[22px]" />
              </div>
            </div>

            {/* Caption */}
            <div className="mt-2 text-center w-full px-1">
              <h4
                className={`font-sans ${
                  isCompany ? "text-[9.5px] sm:text-[10.5px] md:text-xs" : "text-xs"
                } font-semibold text-[#2a1f14] tracking-wide uppercase line-clamp-1`}
              >
                {photo.title}
              </h4>
              <p
                className={`mt-0.5 font-body ${
                  isCompany ? "text-[8px] sm:text-[8.5px] md:text-[9px]" : "text-[9px]"
                } tracking-[0.15em] uppercase text-[#8a7560] line-clamp-1`}
              >
                {photo.caption} <span className="opacity-60">· {photo.year}</span>
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

/* ─────────────────────────────────────────────────────────────
   MAIN HERITAGE GALLERY COMPONENT (Verandah)
───────────────────────────────────────────────────────────── */
const Verandah = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedExhibitId, setSelectedExhibitId] = useState(null);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [galleryItems, setGalleryItems] = useState([]);
  const [isLoadingGallery, setIsLoadingGallery] = useState(true);

  // Fetch gallery items dynamically from backend API
  useEffect(() => {
    let isMounted = true;
    GalleryAPI.getAll()
      .then((data) => {
        if (isMounted && Array.isArray(data)) {
          setGalleryItems(data);
        }
      })
      .catch((err) => console.error("Failed to load dynamic gallery:", err))
      .finally(() => {
        if (isMounted) setIsLoadingGallery(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Compute dynamic exhibits based on fetched items, falling back to initial configs
  const exhibits = useMemo(() => {
    return EXHIBIT_CONFIGS.map((config) => {
      const sectionItems = galleryItems.filter(
        (item) => (item.section || "").toLowerCase() === config.id.toLowerCase() && !item.isDeleted
      );

      const photos =
        sectionItems.length > 0
          ? sectionItems.map((item, idx) => ({
              src: resolveImageUrl(item.imagePath),
              title: item.title || item.caption || "",
              caption: item.caption || item.title || "",
              year: item.createdOn ? new Date(item.createdOn).getFullYear().toString() : "ARCHIVE",
              size: idx === 0 ? "lg" : idx % 2 === 0 ? "md" : "sm",
              galleryId: item.galleryId,
            }))
          : config.fallbackPhotos || [];

      const coverFrames =
        photos.length >= 3
          ? photos.slice(0, 3).map((p) => ({
              src: p.src,
              caption: p.title || p.caption,
              year: p.year,
            }))
          : photos.length > 0
          ? photos.map((p) => ({
              src: p.src,
              caption: p.title || p.caption,
              year: p.year,
            }))
          : config.fallbackCoverFrames;

      const hero = photos.length > 0 ? photos[0].src : config.fallbackHero;

      return {
        ...config,
        hero,
        coverFrames,
        photos,
      };
    });
  }, [galleryItems]);

  const selectedExhibit = selectedExhibitId
    ? exhibits.find((ex) => ex.id === selectedExhibitId) || null
    : null;

  /* Scroll to top when exhibit changes */
  const handleExhibitSelect = (exhibit, index) => {
    setSelectedExhibitId(exhibit.id);
    setActiveTab(index);
    const element = document.getElementById("verandah");
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  const handleBackToGallery = () => {
    setSelectedExhibitId(null);
  };

  return (
    <section
      id="verandah"
      data-chapter
      className="relative bg-[#f5f0e8] text-[#2a1f14] overflow-hidden border-t border-[#33271F]/30"
    >
      {/* Subtle Background Pattern Texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.035]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* ── TOP NAVIGATION BAR (Full Length Header with Accent Underline) ── */}
      <header className="relative z-20 w-full border-b border-[#33271F]/20 bg-[#f5f0e8]/95 backdrop-blur-sm px-6 md:pl-[12%] lg:pl-[14%] xl:pl-[16%] pr-6 md:pr-12 lg:pr-16 pt-5 pb-4 mb-4">
        <div className="w-full flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="font-display text-base font-light text-[#c4a86a]">05</span>
            <span className="h-px w-6 bg-[#c4a86a]" />
            <p className="font-body text-[9.5px] tracking-[0.35em] uppercase text-[#b89a60] font-bold">
              HERITAGE GALLERY · 5 EXHIBITS
            </p>
          </div>

          {/* 5 Exhibit Tabs */}
          <nav className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
            <button
              onClick={handleBackToGallery}
              className={`flex-shrink-0 px-3.5 py-1.5 font-sans text-[9px] tracking-[0.2em] uppercase font-bold rounded border transition-all duration-300 ${
                selectedExhibitId === null
                  ? "border-[#33271F] bg-[#33271F] text-[#f5f0e8] shadow-sm"
                  : "border-[#c4a86a]/40 bg-[#f5f0e8] text-[#5a4535] hover:border-[#33271F] hover:text-[#3a2a1a]"
              }`}
            >
              ALL EXHIBITS
            </button>

            {exhibits.map((ex, i) => (
              <button
                key={ex.id}
                onClick={() => handleExhibitSelect(ex, i)}
                className={`flex-shrink-0 px-3.5 py-1.5 font-sans text-[9px] tracking-[0.2em] uppercase font-bold rounded border transition-all duration-300 ${
                  selectedExhibitId === ex.id
                    ? "border-[#33271F] bg-[#33271F] text-[#f5f0e8] shadow-sm"
                    : "border-[#c4a86a]/40 bg-[#f5f0e8] text-[#5a4535] hover:border-[#33271F] hover:text-[#3a2a1a]"
                }`}
              >
                {ex.num} {ex.title}
              </button>
            ))}
          </nav>
        </div>

        {/* Full-length Accent Underline Bar */}
        <div className="w-full mt-3 h-[3px] rounded-full bg-gradient-to-r from-[#c4a86a] via-[#b89a60] to-[#33271F]" />
      </header>

      {/* ── MAIN CONTENT: LANDING PAGE OR INDIVIDUAL ALBUM ── */}
      <AnimatePresence mode="wait">
        {selectedExhibit ? (
          <IndividualExhibitView
            key="album-view"
            exhibit={selectedExhibit}
            onBack={handleBackToGallery}
            onOpenLightbox={(photo) => setLightboxPhoto(photo)}
          />
        ) : (
          <motion.div
            key="landing-view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full px-6 md:pl-[12%] lg:pl-[14%] xl:pl-[16%] pr-6 md:pr-12 lg:pr-16 py-4 md:py-6"
          >
            {/* 1. FIRST SCREEN — COMPACT MEDIUM GALLERY SUMMARY HERO */}
            <div className="text-center max-w-2xl mx-auto mb-6">
              <div className="flex items-center justify-center gap-2 mb-1">
                <span className="h-px w-6 bg-[#33271F]/40" />
                <span className="font-display text-sm font-light text-[#c4a86a]">05</span>
                <span className="h-px w-6 bg-[#33271F]/40" />
              </div>

              <h1 className="font-display text-3xl md:text-4xl font-normal tracking-tight text-[#2a1f14]">
                GALLERY
              </h1>

              <p className="mt-1 font-cursive text-lg md:text-xl text-[#c4a86a] italic">
                A collection of memories, moments and milestones.
              </p>

              <p className="mt-2 font-body text-xs md:text-sm text-[#5a4535]/80 font-light leading-relaxed max-w-lg mx-auto">
                Step into our world through five curated exhibits celebrating the journey, the people, the flavours and the legacy.
              </p>
            </div>

            {/* Dark Chocolate Separator */}
            <DarkChocolateSeparator />

            {/* 2. GALLERY EXHIBIT LANDING COMPOSITION (Rows matching screenshot) */}
            <div className="my-6 grid grid-cols-1 md:grid-cols-12 gap-5 lg:gap-6">
              {/* Row 1: 3 Large Cards */}
              <EditorialExhibitCard
                exhibit={exhibits[0]}
                onClick={() => handleExhibitSelect(exhibits[0], 0)}
              />
              <EditorialExhibitCard
                exhibit={exhibits[1]}
                onClick={() => handleExhibitSelect(exhibits[1], 1)}
              />
              <EditorialExhibitCard
                exhibit={exhibits[2]}
                onClick={() => handleExhibitSelect(exhibits[2], 2)}
              />

              {/* Row 2: 2 Wide Cards */}
              <EditorialExhibitCard
                exhibit={exhibits[3]}
                onClick={() => handleExhibitSelect(exhibits[3], 3)}
                isWide={true}
              />
              <EditorialExhibitCard
                exhibit={exhibits[4]}
                onClick={() => handleExhibitSelect(exhibits[4], 4)}
                isWide={true}
              />
            </div>

            {/* Dark Chocolate Separator */}
            <DarkChocolateSeparator />

            {/* 9. BOTTOM CLOSING STATEMENT */}
            <div className="text-center py-5">
              <p className="font-cursive text-lg md:text-xl text-[#3a2a1a] italic">
                "Every moment, every recipe, every smile – a part of our story"
              </p>
              <div className="mt-2 flex items-center justify-center gap-3">
                <span className="h-px w-10 bg-[#c4a86a]/40" />
                <span className="font-sans text-[9px] tracking-[0.3em] uppercase text-[#b89a60] font-semibold">
                  EXPLORE THE EXHIBITS
                </span>
                <span className="h-px w-10 bg-[#c4a86a]/40" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* LIGHTBOX MODAL */}
      <AnimatePresence>
        {lightboxPhoto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-md flex items-center justify-center p-6"
            onClick={() => setLightboxPhoto(null)}
          >
            <button
              onClick={() => setLightboxPhoto(null)}
              className="absolute top-6 right-6 text-white/80 hover:text-white"
            >
              <X size={28} />
            </button>
            <div className="max-w-4xl text-center" onClick={(e) => e.stopPropagation()}>
              <img
                src={lightboxPhoto.src}
                alt={lightboxPhoto.title}
                className="max-h-[75vh] mx-auto object-contain rounded border-4 border-[#f5f0e8]"
              />
              <h3 className="mt-4 font-sans text-lg text-white uppercase font-bold tracking-wider">
                {lightboxPhoto.title}
              </h3>
              <p className="text-xs text-[#c4a86a] tracking-widest uppercase mt-1">
                {lightboxPhoto.caption} · {lightboxPhoto.year}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── VISIT FOOTER ─────────────────────────────────── */}
      <VisitFooter />
    </section>
  );
};

export default Verandah;
